<template>
  <CustomModal
    class="update-connectivity-providers-modal"
    :is-modal-open="isUpdateConnectivityProviderOpen"
    @toggleModal="closeUpdateConnectivityProviderModal"
    @onClickAway="closeUpdateConnectivityProviderModal"
    :overlay="'dark'"
    :withHeader="true"
    :withFooter="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-screen tw:md:max-w-[636px] tw:md:w-[636px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader>
      <p class="tw:text-xl tw:text-gray-700 tw:font-bold">
        {{ $t('rc_change_providers_header') }}
      </p>
    </template>

    <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full">
      <div class="tw:w-full">
        <p class="tw:mb-6">
          {{ $t('rc_change_providers_subtext') }}
        </p>
        <p class="tw:font-bold tw:text-sm tw:mb-3">{{ $t('rc_available_providers') }}</p>
        <PartnerRadioCards
          :options="sortedFinalOptions"
          :selectedPartner="selectedPartner"
          @handleOptionChange="handleOptionChange"
        />
      </div>
    </div>
    <template #modalFooter>
      <div class="tw:flex tw:justify-end tw:w-full tw:gap-2">
        <GhostButton size="big" class="tw:px-9" @click="closeUpdateConnectivityProviderModal">{{
          $t('cancel')
        }}</GhostButton>
        <MainButton
          size="big"
          class="tw:px-9 tw:mr-2"
          @click="updatePartner"
          :disabled="!selectedPartner || Number(selectedPartner) === Number(currentPartner)"
        >
          <span v-if="!isUpdating">{{ $t('rc_provider_update_cta') }}</span>
          <span v-if="isUpdating">
            <SmallDotLoader color="#fff" />
          </span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'

import CustomModal from '@/components/base/CustomModal.vue'
import PartnerRadioCards from '@/components/rateConnect/settings/PartnerRadioCards.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

import { useCampaign, usePartners, usePartnersMutation } from '@/components/rateConnect/queries'
import { useRateConnect } from '@/components/rateConnect/useRateConnect.js'
import { usePartnerNames } from '@/components/homePage/queries/conversionFunnel'

import { selectedAccommodationId } from '@/layouts/queries'

import useToastNotifications from '@/components/notifications/useToastNotifications'

const { currentPartner, currentCampaignType } = useCampaign(selectedAccommodationId)
const selectedPartner = ref(null)
const isUpdating = ref(false)
const { changePartner } = usePartnersMutation()
const { displayNotification, showSaveError } = useToastNotifications()
const { isUpdateConnectivityProviderOpen, closeUpdateConnectivityProviderModal } = useRateConnect()
const { partnersData } = usePartners(selectedAccommodationId)
const partnersId = computed(() => {
  let ids = []
  partnersData.value.forEach(item => ids.push(item.partnerId))
  return ids
})

const { partners } = usePartnerNames(partnersId)

watchEffect(() => {
  if (currentPartner.value) {
    selectedPartner.value = currentPartner.value
  }
})

const finalPartnersList = computed(() => {
  return (partnersData.value ?? [])
    ?.filter(partner => (currentCampaignType.value === 'netcpa' ? partner.canNetCpa : true))
    .map(item => {
      const currentName = partners.value.find(partner => partner.nsid.id === item.partnerId)

      return {
        ...item,
        partnerName: currentName?.originalName?.value,
        isActive: item.connectionStatus === 5 && item.partnerStatus === 5,
      }
    })
})

const sortedFinalOptions = computed(() => {
  return [...finalPartnersList.value].sort((a, b) => {
    if (a.isActive && !b.isActive) return -1
    if (!a.isActive && b.isActive) return 1

    return a.partnerName.localeCompare(b.partnerName)
  })
})

const handleOptionChange = partnerId => {
  selectedPartner.value = partnerId
}

const selectedPartnerName = computed(() => {
  const currentName = partners.value.find(item => item.nsid.id === selectedPartner.value)
  return currentName?.originalName?.value
})

const updatePartner = () => {
  changePartner(
    {
      itemId: selectedAccommodationId.value,
      partnerId: selectedPartner.value,
    },
    {
      onSuccess: () => {
        closeUpdateConnectivityProviderModal()
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_connectivity_provider_change_success',
          translationParams: { name: selectedPartnerName.value },
          isTranslationKey: true,
          isHtml: true,
          type: 'success',
        })
      },
      onError: () => {
        showSaveError()
      },
    }
  )
}
</script>
